import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const GuestContext = createContext(null);

export function useGuest() {
  return useContext(GuestContext);
}

// Stage 1: 5 minutes → small banner (auto-dissolves)
// Stage 2: 10 minutes → popup with "Maybe Later"
// Stage 3: 15 minutes → PERMANENT popup (no dismiss)
const STAGE_1_MS = 5 * 60 * 1000;
const STAGE_2_MS = 10 * 60 * 1000;
const STAGE_3_MS = 15 * 60 * 1000;

export function GuestProvider({ children, user, loading }) {
  // Bots and prerender should never be treated as guests
  const isBotOrPrerender = typeof window !== 'undefined' && (
    window.__PRERENDER_INJECTED === true ||
    /Googlebot|Bingbot|UnexaPrerender|baiduspider|facebot|twitterbot|linkedinbot/i.test(navigator.userAgent)
  );
  const isGuest = !isBotOrPrerender && !loading && !user;
  const [stage, setStage] = useState(0);  // 0=free, 1=banner, 2=popup, 3=permanent
  const [showBanner, setShowBanner] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [forceSignup, setForceSignup] = useState(false);
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current = [];
  };

  useEffect(() => {
    if (!isGuest) {
      setShowBanner(false);
      setShowSignup(false);
      setForceSignup(false);
      setStage(0);
      clearTimers();
      // Clear guest timer data on login so returning guests get fresh timer
      localStorage.removeItem('unexa_guest_start');
      localStorage.removeItem('unexa_stage2_dismissed');
      return;
    }

    let guestStart = localStorage.getItem('unexa_guest_start');
    if (!guestStart) {
      guestStart = Date.now().toString();
      localStorage.setItem('unexa_guest_start', guestStart);
    }

    const start = parseInt(guestStart);
    const elapsed = Date.now() - start;

    // Already past stage 3
    if (elapsed >= STAGE_3_MS) {
      setStage(3);
      setForceSignup(true);
      setShowSignup(true);
      return clearTimers;
    }

    // Already past stage 2
    if (elapsed >= STAGE_2_MS) {
      const dismissed = localStorage.getItem('unexa_stage2_dismissed') === 'true';
      if (dismissed) {
        const remaining3 = STAGE_3_MS - elapsed;
        timersRef.current.push(setTimeout(() => {
          setStage(3);
          setForceSignup(true);
          setShowSignup(true);
        }, remaining3));
      } else {
        setStage(2);
        setShowSignup(true);
        const remaining3 = STAGE_3_MS - elapsed;
        timersRef.current.push(setTimeout(() => {
          setStage(3);
          setForceSignup(true);
          setShowSignup(true);
        }, remaining3));
      }
      return clearTimers;
    }

    // Already past stage 1
    if (elapsed >= STAGE_1_MS) {
      const remaining2 = STAGE_2_MS - elapsed;
      const remaining3 = STAGE_3_MS - elapsed;
      timersRef.current.push(setTimeout(() => {
        setStage(2);
        setShowSignup(true);
      }, remaining2));
      timersRef.current.push(setTimeout(() => {
        setStage(3);
        setForceSignup(true);
        setShowSignup(true);
      }, remaining3));
      return clearTimers;
    }

    // Fresh guest — schedule all 3 stages
    const remaining1 = STAGE_1_MS - elapsed;
    const remaining2 = STAGE_2_MS - elapsed;
    const remaining3 = STAGE_3_MS - elapsed;

    // Stage 1: Show banner at 10 seconds
    timersRef.current.push(setTimeout(() => {
      setStage(1);
      setShowBanner(true);
      // Auto-dissolve banner after 5 seconds
      timersRef.current.push(setTimeout(() => {
        setShowBanner(false);
      }, 5000));
    }, remaining1));

    // Stage 2: Show popup at 2 minutes
    timersRef.current.push(setTimeout(() => {
      setStage(2);
      setShowSignup(true);
    }, remaining2));

    // Stage 3: Permanent popup at 3 minutes
    timersRef.current.push(setTimeout(() => {
      setStage(3);
      setForceSignup(true);
      setShowSignup(true);
    }, remaining3));

    return clearTimers;
  }, [isGuest]);

  const requireAuth = useCallback(() => {
    if (isGuest) {
      setShowSignup(true);
      return true;
    }
    return false;
  }, [isGuest]);

  const handleMaybeLater = useCallback(() => {
    if (forceSignup) return;
    setShowSignup(false);
    localStorage.setItem('unexa_stage2_dismissed', 'true');
  }, [forceSignup]);

  const dismissBanner = useCallback(() => {
    setShowBanner(false);
  }, []);

  const dismissSignup = useCallback(() => {
    if (forceSignup) return;
    setShowSignup(false);
  }, [forceSignup]);

  return (
    <GuestContext.Provider value={{
      isGuest,
      stage,
      showBanner,
      showSignup,
      forceSignup,
      requireAuth,
      handleMaybeLater,
      dismissBanner,
      dismissSignup,
      setShowSignup,
    }}>
      {children}
    </GuestContext.Provider>
  );
}
